import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import { Card, CircularProgress } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const PLACEHOLDER_IMG = "https://via.placeholder.com/500x500.png?text=Sem+Imagem";

function formatNum(v, dec = 2) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

function InfoItem({ label, value }) {
  if (value === null || value === undefined || value === "") return null;

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" sx={{ fontSize: 11 }}>
        {label}
      </MDTypography>
      <MDTypography variant="button" display="block" sx={{ fontSize: 13 }}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

export default function FichaProduto() {
  const { refmaterial } = useParams();
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const idUtilizador = Number(userData?.id || 0);

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);

  const material = dados?.material || null;
  const stocks = Array.isArray(dados?.stocks) ? dados.stocks : [];
  const precos = dados?.precos || null;
  const imagemUrl = dados?.imagem?.url || PLACEHOLDER_IMG;

  const carregarFicha = async () => {
    const ref = String(refmaterial || "").trim();

    if (!ref) {
      showAlert("Referência de material inválida.", "warning");
      return;
    }

    setLoading(true);

    const [res, err] = await handleApi(
      api.post("Stocks/SelFichaProduto", {
        refmaterial: ref,
        idutilizador: idUtilizador,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      setDados(null);
      return;
    }

    setDados(res || null);
  };

  useEffect(() => {
    carregarFicha();
  }, [refmaterial, idUtilizador]);

  return (
    <MDBox
      sx={{
        minHeight: "100vh",
        background: "#f5f6fa",
        p: { xs: 1.5, md: 3 },
      }}
    >
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      {!loading && !material && (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5">Material não encontrado</MDTypography>
          </MDBox>
        </Card>
      )}

      {material && (
        <>
          <Card>
            <MDBox
              p={{ xs: 2, md: 3 }}
              display="grid"
              gap={3}
              sx={{
                gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
              }}
            >
              <MDBox
                sx={{
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imagemUrl}
                  alt={material.nome || material.refmaterial}
                  style={{
                    width: "100%",
                    maxHeight: 360,
                    objectFit: "contain",
                  }}
                />
              </MDBox>

              <MDBox>
                <MDTypography variant="h4" sx={{ lineHeight: 1.15 }}>
                  {material.nome}
                </MDTypography>

                <MDTypography variant="h6" color="text" mt={0.5}>
                  Ref.: {material.refmaterial}
                </MDTypography>

                <MDBox
                  mt={2}
                  display="grid"
                  gap={1.25}
                  sx={{
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                  }}
                >
                  <InfoItem label="Fabricante" value={material.fabricante} />
                  <InfoItem label="Família" value={material.familia} />
                  <InfoItem label="Subfamília" value={material.subfamilia} />
                  <InfoItem label="Marca" value={material.marca} />
                  <InfoItem label="Submarca" value={material.submarca} />
                  <InfoItem label="Tipo" value={material.tipo} />
                  <InfoItem label="Segmento" value={material.segmento} />
                  <InfoItem label="Grupo" value={material.grupo} />
                  <InfoItem label="Capacidade" value={material.capacidade} />
                  <InfoItem label="Unidade" value={material.unidade} />
                  <InfoItem label="Volume" value={formatNum(material.volume, 3)} />
                  <InfoItem label="Peso líquido" value={formatNum(material.pesoliquido, 3)} />
                  <InfoItem label="Peso bruto" value={formatNum(material.pesobruto, 3)} />
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>

          <MDBox mt={2}>
            <Card>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDTypography variant="h6" mb={1.5}>
                  Preços
                </MDTypography>

                <MDBox
                  display="grid"
                  gap={1.5}
                  sx={{
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr 1fr" },
                  }}
                >
                  <InfoItem
                    label="PVP recomendado"
                    value={
                      precos?.zpr0 !== null && precos?.zpr0 !== undefined
                        ? `${formatNum(precos.zpr0)} €`
                        : ""
                    }
                  />

                  {isAdminLike && (
                    <>
                      <InfoItem
                        label="Preço de custo"
                        value={
                          precos?.zpvr !== null && precos?.zpvr !== undefined
                            ? `${formatNum(precos.zpvr)} €`
                            : ""
                        }
                      />

                      <InfoItem
                        label="Margem"
                        value={
                          precos?.zmrg !== null && precos?.zmrg !== undefined
                            ? `${formatNum(precos.zmrg)}`
                            : ""
                        }
                      />

                      <InfoItem
                        label="Desconto máximo autorizado"
                        value={
                          material.descontopromocional !== null &&
                          material.descontopromocional !== undefined
                            ? `${formatNum(material.descontopromocional, 2)} %`
                            : "Sem valor definido"
                        }
                      />
                    </>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>

          <MDBox mt={2}>
            <Card>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDTypography variant="h6" mb={1.5}>
                  Stocks por centro
                </MDTypography>

                <MDBox sx={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      minWidth: 640,
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Centro</th>
                        <th style={thStyle}>Ref. centro</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Qtd oficial</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Qtd verificada</th>
                        <th style={thStyle}>Unidade</th>
                      </tr>
                    </thead>

                    <tbody>
                      {stocks.map((s, idx) => (
                        <tr key={`${s.refcentro}-${idx}`}>
                          <td style={tdStyle}>{s.centro}</td>
                          <td style={tdStyle}>{s.refcentro}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>
                            {formatNum(s.quantidade, 2)}
                          </td>

                          <td
                            style={{
                              ...tdStyle,
                              textAlign: "right",
                              color:
                                s.qtd_verificada !== null &&
                                s.qtd_verificada !== undefined &&
                                Number(s.qtd_verificada) !== Number(s.quantidade)
                                  ? "#d32f2f"
                                  : undefined,
                              fontWeight:
                                s.qtd_verificada !== null &&
                                s.qtd_verificada !== undefined &&
                                Number(s.qtd_verificada) !== Number(s.quantidade)
                                  ? 700
                                  : 400,
                            }}
                          >
                            {s.qtd_verificada !== null && s.qtd_verificada !== undefined
                              ? formatNum(s.qtd_verificada, 2)
                              : "—"}
                          </td>

                          <td style={tdStyle}>{s.refunidade}</td>
                        </tr>
                      ))}

                      {stocks.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              ...tdStyle,
                              textAlign: "center",
                              color: "#7b809a",
                            }}
                          >
                            Sem stocks registados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </>
      )}
    </MDBox>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #e0e0e0",
  fontSize: 12,
  color: "#7b809a",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};
